import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@mui/material";
import { connect } from "react-redux";
import TextInput from "./TextInput";
import SelectYear from "./SelectYear";
import { getCurrentYear } from "@instream-solutions/instream-design-system/lib/Utilities/shared";
import { getDefaultLifeExpectancyDate } from "../utils";

const TAXCREDITS_OPTION = "TAXCREDITS_OPTION";
const CAPITALGAINS_OPTION = "CAPITALGAINS_OPTION";
const PROPERTYTAX_OPTION = "PROPERTYTAX_OPTION";

const TaxOptionsTable = (props) => {
  const [taxOptions, setTaxOptions] = useState([
    {
      ...props.taxes.planWizardTaxCreditViewList[0],
      taxOptionType: TAXCREDITS_OPTION,
      isEdited: false,
    },
    {
      ...props.taxes.planWizardCapitalGainsViewList[0],
      taxOptionType: CAPITALGAINS_OPTION,
      isEdited: false,
    },
    {
      ...props.taxes.planPropertyTaxViewList[0],
      taxOptionType: PROPERTYTAX_OPTION,
      isEdited: false,
    },
  ]);

  useEffect(() => {
    const payload = {
      ...props.taxes,
      planWizardTaxCreditViewList: [
        {
          setPlanWizardTaxCreditId: null,
          amount: taxOptions[0].amount,
          date: {
            startDateType: "REGULAR_DATE",
            endDateType: "REGULAR_DATE",
            relativeStartDateNum: 0,
            relativeEndDateNum: 0,
            startYear: taxOptions[0].date.startYear,
            endYear: taxOptions[0].date.endYear,
            startMonth: 1,
            endMonth: 12,
          },
        },
      ],
      planWizardCapitalGainsViewList: [
        {
          taxPeriodId: null,
          amount: taxOptions[1].amount,
          date: {
            startDateType: "REGULAR_DATE",
            endDateType: "REGULAR_DATE",
            relativeStartDateNum: 0,
            relativeEndDateNum: 0,
            startYear: taxOptions[1].date.startYear,
            endYear: taxOptions[1].date.endYear,
            startMonth: 1,
            endMonth: 12,
          },
        },
      ],
      planPropertyTaxViewList: [
        {
          propertyTaxId: null,
          amount: taxOptions[2].amount,
          date: {
            startDateType: "REGULAR_DATE",
            endDateType: "REGULAR_DATE",
            relativeStartDateNum: 0,
            relativeEndDateNum: 0,
            startYear: taxOptions[2].date.startYear,
            endYear: taxOptions[2].date.endYear,
            startMonth: 1,
            endMonth: 12,
          },
        },
      ],
    };

    props.updateTaxes(payload);
  }, [taxOptions]);

  const columns = [
    { id: "type", label: "Type of Tax Option", minWidth: 170 },
    {
      id: "monthly",
      label: "Amount",
      minWidth: 170,
    },
    {
      id: "timeInterval",
      label: "Time interval",
      minWidth: 300,
    },
  ];

  // client details
  const client = useMemo(() => props.currentClient.get("client").toJS(), [
    props.currentClient,
  ]);
  const coClient = useMemo(() => props.currentClient.get("coClient").toJS(), [
    props.currentClient,
  ]);

  const planLength = useMemo(() => {
    const lifeExpectancyDate = getDefaultLifeExpectancyDate(
      null,
      client,
      coClient
    );
    const lifeExpectancyYear = lifeExpectancyDate.getFullYear();
    return lifeExpectancyYear - getCurrentYear() + 1;
  }, [client]);

  const validateTaxOptionsDate = (index) => {
    return taxOptions[index].date.startYear <= taxOptions[index].date.endYear;
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>
            <Typography>
              What amount does the client claim for Tax Credit(s)?
            </Typography>
          </TableCell>
          <TableCell>
            <TextInput
              type="number"
              value={taxOptions[0].amount}
              adornment="$"
              onChange={(e) => {
                setTaxOptions((v) =>
                  v.map((item) => {
                    if (item.taxOptionType !== TAXCREDITS_OPTION) return item;
                    return {
                      ...item,
                      amount: e.target.value,
                      isEdited: true,
                    };
                  })
                );
              }}
              isRequired={taxOptions[0].isEdited}
            />
          </TableCell>
          <TableCell>
            <Grid item container direction="row" spacing="3">
              <Grid item container direction="row">
                <Grid item>
                  <Typography component="div" className="year-label">
                    Start &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    value={taxOptions[0].date.startYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== TAXCREDITS_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              startYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(0)}
                    isRequired={taxOptions[0].isEdited}
                  />
                </Grid>
                <Grid item>
                  <Typography component="div" className="year-label">
                    End &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    from={taxOptions[0].date.startYear}
                    value={taxOptions[0].date.endYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== TAXCREDITS_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              endYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(0)}
                    isRequired={taxOptions[0].isEdited}
                  />
                </Grid>
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Typography>
              What amount does the client claim for Capital Gains?
            </Typography>
          </TableCell>
          <TableCell>
            <TextInput
              type="number"
              value={taxOptions[1].amount}
              adornment="$"
              onChange={(e) => {
                setTaxOptions((v) =>
                  v.map((item) => {
                    if (item.taxOptionType !== CAPITALGAINS_OPTION) return item;
                    return {
                      ...item,
                      amount: e.target.value,
                      isEdited: true,
                    };
                  })
                );
              }}
              isRequired={taxOptions[1].isEdited}
            />
          </TableCell>
          <TableCell>
            <Grid item container direction="row" spacing="3">
              <Grid item container direction="row">
                <Grid item>
                  <Typography component="div" className="year-label">
                    Start &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    value={taxOptions[1].date.startYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== CAPITALGAINS_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              startYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(1)}
                    isRequired={taxOptions[1].isEdited}
                  />
                </Grid>
                <Grid item>
                  <Typography component="div" className="year-label">
                    End &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    from={taxOptions[1].date.startYear}
                    value={taxOptions[1].date.endYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== CAPITALGAINS_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              endYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(1)}
                    isRequired={taxOptions[1].isEdited}
                  />
                </Grid>
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Typography>
              What amount does the client claim for Property Tax?
            </Typography>
          </TableCell>
          <TableCell>
            <TextInput
              type="number"
              value={taxOptions[2].amount}
              adornment="$"
              onChange={(e) => {
                setTaxOptions((v) =>
                  v.map((item) => {
                    if (item.taxOptionType !== PROPERTYTAX_OPTION) return item;
                    return {
                      ...item,
                      amount: e.target.value,
                      isEdited: true,
                    };
                  })
                );
              }}
              isRequired={taxOptions[2].isEdited}
            />
          </TableCell>
          <TableCell>
            <Grid item container direction="row" spacing="3">
              <Grid item container direction="row">
                <Grid item>
                  <Typography component="div" className="year-label">
                    Start &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    value={taxOptions[2].date.startYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== PROPERTYTAX_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              startYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(2)}
                    isRequired={taxOptions[2].isEdited}
                  />
                </Grid>
                <Grid item>
                  <Typography component="div" className="year-label">
                    End &nbsp;
                  </Typography>
                </Grid>
                <Grid item>
                  <SelectYear
                    from={taxOptions[2].date.startYear}
                    value={taxOptions[2].date.endYear}
                    onChange={(e) => {
                      setTaxOptions((v) =>
                        v.map((item) => {
                          if (item.taxOptionType !== PROPERTYTAX_OPTION)
                            return item;
                          return {
                            ...item,
                            date: {
                              ...item.date,
                              endYear: e.target.value,
                            },
                            isEdited: true,
                          };
                        })
                      );
                    }}
                    length={planLength}
                    error={!validateTaxOptionsDate(2)}
                    isRequired={taxOptions[2].isEdited}
                  />
                </Grid>
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

const mapStateToProps = (state) => {
  return {
    currentClient: state.currentClientReducer,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(TaxOptionsTable);
